<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { KindIcon, Skeleton } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { entryTone, type TransactionRow } from '../internal/transaction-row.ts'

/** The latest entries already posted, beside a form that adds more - so a duplicate is caught before it is typed. */
const props = defineProps<{
  rows: readonly TransactionRow[]
  isLoading: boolean
  error: Error | null
  heading?: string | undefined
}>()

const RECENT_COUNT = 5

const { t } = useI18n()

const heading = computed(() => props.heading ?? t('entries.alreadyInLedger'))

const KIND_TILE_CLASS = {
  INCOME: 'bg-[color:color-mix(in_srgb,var(--color-income)_12%,transparent)]',
  EXPENSE: 'bg-[color:color-mix(in_srgb,var(--color-expense)_12%,transparent)]',
  TRANSFER: 'bg-[color:color-mix(in_srgb,var(--color-transfer)_12%,transparent)]',
} as const

function view(row: TransactionRow) {
  const isTransfer = row.kind === 'TRANSFER'
  return {
    title: row.title || t(isTransfer ? 'transactions.transfer' : 'transactions.noDescription'),
    account: row.account?.leaf ?? '',
    kindLabel:
      row.kind === undefined ? t('transactions.noKind') : t(`transactions.${row.kind.toLowerCase()}`),
    tileClass: row.kind === undefined ? 'bg-[color:var(--color-surface-sunken)]' : KIND_TILE_CLASS[row.kind],
    tone: row.total === undefined ? undefined : entryTone(row.kind, row.total),
    total: row.total,
    signed: !isTransfer,
  }
}

/** Each recent row paired with its view, computed once per row so the template narrows `total` in one place. */
const recent = computed(() => props.rows.slice(0, RECENT_COUNT).map((row) => ({ row, shown: view(row) })))
</script>

<template>
  <section class="flex flex-col gap-[var(--space-inline)]" :aria-label="heading">
    <h2 class="text-[length:var(--text-step-2)] font-semibold text-[color:var(--color-text-muted)]">
      {{ heading }}
    </h2>
    <ul v-if="isLoading" aria-hidden="true" class="flex flex-col gap-[var(--space-inline)]">
      <li v-for="index in 3" :key="index"><Skeleton height="1.75rem" /></li>
    </ul>
    <p v-else-if="error" class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
      {{ t('entries.alreadyInLedgerUnavailable') }}
    </p>
    <p
      v-else-if="rows.length === 0"
      class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
    >
      {{ t('transactions.emptyTitle') }}
    </p>
    <ul v-else class="flex flex-col">
      <li
        v-for="{ row, shown } in recent"
        :key="row.id"
        class="flex min-w-0 items-center gap-[var(--space-inline)] border-t border-[color:var(--color-border)] py-[var(--space-inline)] first:border-t-0"
      >
        <span
          class="flex h-6 w-6 shrink-0 items-center justify-center rounded-[var(--radius-control)]"
          :class="shown.tileClass"
        >
          <KindIcon :kind="row.kind" :label="shown.kindLabel" />
        </span>
        <span class="min-w-0 flex-1">
          <span
            class="block truncate text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]"
          >
            {{ shown.title }}
          </span>
          <span class="block truncate text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
            {{ shown.account }}
          </span>
        </span>
        <span class="shrink-0">
          <MoneyAmount
            v-if="shown.total !== undefined"
            :value="shown.total"
            :sign="shown.signed"
            :tone="shown.tone"
          />
          <span v-else class="text-[color:var(--color-text-muted)]">—</span>
        </span>
      </li>
    </ul>
  </section>
</template>
